import "./styles.css";
import QRCode from "qrcode";
import {
  adminApi,
  clearSession,
  getApiBase,
  getToken,
  publicApi,
  setApiBase,
  setToken,
  setOpPassword,
} from "./api";

const app = document.querySelector<HTMLDivElement>("#app")!;

type Tab =
  | "dashboard"
  | "codes"
  | "users"
  | "leaderboard"
  | "settings"
  | "account"
  | "audit";

const TAB_NAMES: Record<Tab, string> = {
  dashboard: "控制台",
  codes: "兑换码",
  users: "付费用户",
  leaderboard: "排行榜治理",
  settings: "服务设置",
  account: "管理员安全",
  audit: "审计日志",
};

declare global {
  interface Window {
    lucide?: { createIcons: (opts?: { attrs?: Record<string, string> }) => void };
  }
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
let modalConfirm: (() => void | Promise<void>) | null = null;

function icons() {
  window.lucide?.createIcons();
}

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function ensureToastEl(): HTMLElement {
  let t = document.getElementById("global-toast");
  if (!t) {
    t = document.createElement("div");
    t.id = "global-toast";
    t.className = "toast";
    t.innerHTML = `<i data-lucide="circle-check"></i><span></span>`;
    document.body.appendChild(t);
  }
  return t;
}

function toast(text: string, ok?: boolean) {
  const t = ensureToastEl();
  const icon = t.querySelector("i");
  const span = t.querySelector("span");
  if (icon) {
    icon.setAttribute(
      "data-lucide",
      ok === false ? "circle-alert" : "circle-check",
    );
  }
  if (span) span.textContent = text;
  t.className = "toast show" + (ok === false ? " err" : "");
  icons();
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    t.classList.remove("show");
  }, 2200);
}

function downloadCsv(filename: string, rows: string[][]) {
  const body = rows
    .map((r) =>
      r
        .map((c) => {
          const s = String(c ?? "");
          if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function ensureModalLayer(): HTMLElement {
  let layer = document.getElementById("modal-layer");
  if (!layer) {
    layer = document.createElement("div");
    layer.id = "modal-layer";
    layer.className = "modal-layer";
    layer.innerHTML = `
      <div class="modal" id="modal-box">
        <div class="modal-head">
          <h2 id="modal-title">确认</h2>
          <button class="icon-btn ghost" type="button" id="modal-close" title="关闭">
            <i data-lucide="x"></i>
          </button>
        </div>
        <div class="modal-body" id="modal-body"></div>
        <div class="modal-foot">
          <button class="btn" type="button" id="modal-cancel">取消</button>
          <button class="btn primary" type="button" id="modal-confirm">确认</button>
        </div>
      </div>`;
    document.body.appendChild(layer);
    layer.addEventListener("click", (e) => {
      if (e.target === layer) closeModal();
    });
    layer.querySelector("#modal-close")!.addEventListener("click", closeModal);
    layer.querySelector("#modal-cancel")!.addEventListener("click", closeModal);
    layer.querySelector("#modal-confirm")!.addEventListener("click", () => {
      const action = modalConfirm;
      // Run while modal DOM still has form values, then close on success.
      if (action) {
        void (async () => {
          try {
            await action();
            closeModal();
          } catch {
            /* keep modal open; handlers toast errors */
          }
        })();
      } else {
        closeModal();
      }
    });
  }
  return layer;
}

function openModal(
  title: string,
  bodyHtml: string,
  opts?: {
    confirmLabel?: string;
    danger?: boolean;
    wide?: boolean;
    /** Called while modal DOM is still present (before close). */
    onConfirm?: () => void | Promise<void>;
  },
) {
  const layer = ensureModalLayer();
  const box = layer.querySelector("#modal-box")!;
  box.className = "modal" + (opts?.wide ? " wide" : "");
  (layer.querySelector("#modal-title") as HTMLElement).textContent = title;
  (layer.querySelector("#modal-body") as HTMLElement).innerHTML = bodyHtml;
  const confirmBtn = layer.querySelector("#modal-confirm") as HTMLButtonElement;
  confirmBtn.textContent = opts?.confirmLabel || "确认";
  confirmBtn.className = "btn " + (opts?.danger ? "danger" : "primary");
  modalConfirm = opts?.onConfirm || null;
  layer.classList.add("open");
  icons();
}

function closeModal() {
  document.getElementById("modal-layer")?.classList.remove("open");
  modalConfirm = null;
}

/** Read a field inside the open modal body. */
function modalField<T extends HTMLElement>(id: string): T | null {
  return document.querySelector(`#modal-layer #${id}`);
}

const ADMIN_THEME_KEY = "velo_admin_theme";
const ADMIN_THEME_MODES = ["system", "light", "dark"] as const;
type AdminThemeMode = (typeof ADMIN_THEME_MODES)[number];
const ADMIN_THEME_LABELS: Record<AdminThemeMode, string> = {
  system: "跟随系统",
  light: "浅色",
  dark: "深色",
};
const ADMIN_THEME_ICONS: Record<AdminThemeMode, string> = {
  system: "monitor",
  light: "sun",
  dark: "moon",
};

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readAdminThemeMode(): AdminThemeMode {
  try {
    const s = localStorage.getItem(ADMIN_THEME_KEY) || "system";
    return (ADMIN_THEME_MODES as readonly string[]).includes(s)
      ? (s as AdminThemeMode)
      : "system";
  } catch {
    return "system";
  }
}

/** Apply theme mode (system|light|dark). Icon shows mode, not only effective color. */
function applyTheme() {
  const root = document.documentElement;
  const mode = readAdminThemeMode();
  const dark =
    mode === "dark" || (mode === "system" && systemPrefersDark());
  root.dataset.themeMode = mode;
  if (dark) root.dataset.theme = "dark";
  else delete root.dataset.theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", dark ? "#17191e" : "#f4f5f7");
  const btn = document.getElementById("theme-toggle");
  if (btn) {
    btn.innerHTML = `<i data-lucide="${ADMIN_THEME_ICONS[mode]}"></i>`;
    btn.title = `主题：${ADMIN_THEME_LABELS[mode]}（点击切换：跟随系统 / 浅色 / 深色）`;
    btn.setAttribute(
      "aria-label",
      `当前主题 ${ADMIN_THEME_LABELS[mode]}，点击切换`,
    );
    icons();
  }
}

function initTheme() {
  applyTheme();
  try {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => {
        if (readAdminThemeMode() === "system") applyTheme();
      });
  } catch {
    /* ignore */
  }
}

function toggleTheme() {
  const cur = readAdminThemeMode();
  const idx = ADMIN_THEME_MODES.indexOf(cur);
  const next = ADMIN_THEME_MODES[(idx + 1) % ADMIN_THEME_MODES.length]!;
  try {
    if (next === "system") localStorage.removeItem(ADMIN_THEME_KEY);
    else localStorage.setItem(ADMIN_THEME_KEY, next);
  } catch {
    /* ignore */
  }
  applyTheme();
}

function themeIcon(): string {
  return ADMIN_THEME_ICONS[readAdminThemeMode()];
}

function initials(text: string): string {
  const s = text.trim();
  if (!s) return "OP";
  if (/@/.test(s)) {
    const local = s.split("@")[0] || s;
    return local.slice(0, 2).toUpperCase();
  }
  return s.slice(0, 2).toUpperCase();
}

function statusBadge(status: string): string {
  const st = status.toLowerCase();
  if (st === "unused") return `<span class="badge blue">未使用</span>`;
  if (st === "redeemed") return `<span class="badge green">已兑换</span>`;
  if (st === "revoked") return `<span class="badge red">已撤销</span>`;
  return `<span class="badge">${esc(status)}</span>`;
}

function planLabel(plan: unknown): string {
  const p = String(plan || "");
  const map: Record<string, string> = {
    lifetime: "Lifetime",
    sub_yearly: "Annual",
    sub_quarterly: "Quarterly",
    sub_monthly: "Monthly",
  };
  return map[p] || p || "—";
}

function todayLabel(): string {
  const d = new Date();
  return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

/* ═══════════════════════════════════════════
   Login
   ═══════════════════════════════════════════ */

async function renderLogin() {
  app.innerHTML = `
    <button class="icon-btn login-theme" type="button" id="theme-toggle" title="切换主题">
      <i data-lucide="${themeIcon()}"></i>
    </button>
    <div class="login-screen">
      <div class="login-wrap">
        <div class="login-brand">
          <span class="brand-mark"><i data-lucide="mouse-pointer-2"></i></span>
          <div>
            <b>Velo Ops</b>
            <span>License console · 运营控制台</span>
          </div>
        </div>
        <div class="login-card" id="login-card">
          <div class="form-head">
            <h2>加载鉴权状态…</h2>
            <p>正在连接 License API</p>
          </div>
          <div class="form-body"><p class="muted">请稍候</p></div>
        </div>
      </div>
    </div>`;
  icons();
  document.getElementById("theme-toggle")!.addEventListener("click", toggleTheme);

  const card = app.querySelector("#login-card")!;
  try {
    setApiBase(getApiBase());
    const st = await publicApi("/admin/auth/status");
    const hasAccounts = Boolean(st.hasAccounts);
    const breakglass = Boolean(st.breakglass);

    if (!hasAccounts) {
      card.innerHTML = `
        <div class="form-head">
          <h2>首次初始化</h2>
          <p>创建运营账号（登录密码 + 可选操作密码 / 邮箱）</p>
        </div>
        <div class="form-body">
          <div class="gate-warn">
            <i data-lucide="shield-alert"></i>
            <span>${breakglass ? "需填写 ADMIN_TOKEN 授权创建。" : "本地 DEV 可直接创建。"} 之后请用账号密码登录；2FA 可在「管理员安全」开启。</span>
          </div>
          <div class="field">
            <label>License API</label>
            <input class="input mono" id="api" type="url" value="${esc(getApiBase())}" />
          </div>
          ${
            breakglass
              ? `<div class="field">
                  <label>ADMIN_TOKEN（破窗）</label>
                  <input class="input mono" id="boot-token" type="password" autocomplete="off" />
                </div>`
              : ""
          }
          <div class="field">
            <label>用户名</label>
            <input class="input" id="user" type="text" autocomplete="username" placeholder="admin" />
          </div>
          <div class="field">
            <label>登录密码（≥10 位）</label>
            <input class="input" id="pass" type="password" autocomplete="new-password" />
          </div>
          <div class="field">
            <label>操作密码（可选）</label>
            <input class="input" id="op" type="password" autocomplete="new-password" />
          </div>
          <div class="field">
            <label>绑定邮箱（可选）</label>
            <input class="input" id="email" type="email" autocomplete="email" />
          </div>
        </div>
        <div class="form-actions">
          <button class="btn primary" id="btn-boot" type="button">创建并进入</button>
        </div>`;
      icons();
      app.querySelector("#btn-boot")!.addEventListener("click", async () => {
        try {
          setApiBase((app.querySelector("#api") as HTMLInputElement).value.trim());
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };
          const bootTok = app.querySelector<HTMLInputElement>("#boot-token");
          if (bootTok?.value.trim()) {
            headers.Authorization = `Bearer ${bootTok.value.trim()}`;
          }
          const body = {
            username: (app.querySelector("#user") as HTMLInputElement).value,
            password: (app.querySelector("#pass") as HTMLInputElement).value,
            opPassword:
              (app.querySelector("#op") as HTMLInputElement).value || undefined,
            email:
              (app.querySelector("#email") as HTMLInputElement).value || undefined,
          };
          const res = await fetch(`${getApiBase()}/admin/auth/bootstrap`, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
          });
          const data = (await res.json()) as Record<string, unknown>;
          if (!res.ok || data.ok === false) {
            throw new Error(String(data.message || data.error || res.status));
          }
          if (data.token) setToken(String(data.token));
          if (body.opPassword) setOpPassword(body.opPassword);
          renderConsole("dashboard");
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });
      return;
    }

    card.innerHTML = `
      <div class="form-head">
        <h2>管理员登录</h2>
        <p>密码 + 可选 2FA · 或破窗 ADMIN_TOKEN</p>
      </div>
      <div class="form-body">
        <div class="field">
          <label>License API</label>
          <input class="input mono" id="api" type="url" value="${esc(getApiBase())}" />
        </div>
        <div class="field">
          <label>用户名</label>
          <input class="input" id="user" type="text" autocomplete="username" />
        </div>
        <div class="field">
          <label>登录密码</label>
          <input class="input" id="pass" type="password" autocomplete="current-password" />
        </div>
        <div class="field">
          <label>两步验证码（已开启 2FA 时必填）</label>
          <input class="input mono" id="totp" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="6 位" />
        </div>
      </div>
      <div class="form-actions">
        <button class="btn primary" id="btn-login" type="button">登录</button>
      </div>
      <div class="form-body" style="padding-top:0">
        <div class="login-divider">或破窗令牌</div>
        <div class="field">
          <label>ADMIN_TOKEN</label>
          <input class="input mono" id="token" type="password" placeholder="Bearer token" autocomplete="off" />
        </div>
      </div>
      <div class="form-actions" style="border-top:0;padding-top:0">
        <button class="btn" id="btn-token" type="button">用 Token 进入</button>
      </div>`;
    icons();

    app.querySelector("#btn-login")!.addEventListener("click", async () => {
      try {
        setApiBase((app.querySelector("#api") as HTMLInputElement).value.trim());
        const data = await publicApi("/admin/auth/login", {
          method: "POST",
          json: {
            username: (app.querySelector("#user") as HTMLInputElement).value,
            password: (app.querySelector("#pass") as HTMLInputElement).value,
            totp:
              (app.querySelector("#totp") as HTMLInputElement).value || undefined,
          },
        });
        setToken(String(data.token || ""));
        renderConsole("dashboard");
      } catch (e) {
        const err = e as Error & { data?: { needTotp?: boolean } };
        if (err.data?.needTotp) {
          toast("请填写两步验证码", false);
        } else {
          toast(String(err.message || e), false);
        }
      }
    });

    app.querySelector("#btn-token")!.addEventListener("click", async () => {
      try {
        setApiBase((app.querySelector("#api") as HTMLInputElement).value.trim());
        setToken((app.querySelector("#token") as HTMLInputElement).value.trim());
        await adminApi("/admin/health");
        renderConsole("dashboard");
      } catch (e) {
        clearSession();
        toast(String((e as Error).message || e), false);
      }
    });

    app.querySelector("#pass")?.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") {
        (app.querySelector("#btn-login") as HTMLButtonElement).click();
      }
    });
  } catch (e) {
    card.innerHTML = `
      <div class="form-head">
        <h2>无法连接 API</h2>
        <p>请确认 License Worker 已启动</p>
      </div>
      <div class="form-body">
        <div class="gate-warn">
          <i data-lucide="triangle-alert"></i>
          <span>${esc((e as Error).message || e)}</span>
        </div>
        <div class="field">
          <label>License API</label>
          <input class="input mono" id="api" type="url" value="${esc(getApiBase())}" />
        </div>
      </div>
      <div class="form-actions">
        <button class="btn primary" id="btn-retry" type="button">重试</button>
      </div>`;
    icons();
    app.querySelector("#btn-retry")!.addEventListener("click", () => {
      setApiBase((app.querySelector("#api") as HTMLInputElement).value.trim());
      void renderLogin();
    });
  }
}

/* ═══════════════════════════════════════════
   Console shell
   ═══════════════════════════════════════════ */

function renderConsole(tab: Tab) {
  let codesPage = 0;
  const pageSize = 50;
  let lastCodes: string[] = [];
  let lastBatchId = "";
  let pageCodes: Array<Record<string, unknown>> = [];
  let currentTab = tab;
  let adminLabel = "ops";
  let adminSubtitle = "管理员";

  const shell = () => {
    app.innerHTML = `
    <div class="app">
      <aside class="sidebar">
        <div class="brand">
          <span class="brand-mark"><i data-lucide="mouse-pointer-2"></i></span>
          <span class="brand-copy"><b>Velo Ops</b><span>License console</span></span>
          <span class="env" title="${esc(getApiBase())}">OPS</span>
        </div>
        <div class="side-scroll">
          <div class="nav-label">概览</div>
          <button class="nav-item" data-view="dashboard" type="button">
            <i data-lucide="layout-dashboard"></i>控制台
          </button>
          <div class="nav-label">授权运营</div>
          <button class="nav-item" data-view="codes" type="button">
            <i data-lucide="ticket-check"></i>兑换码
          </button>
          <button class="nav-item" data-view="users" type="button">
            <i data-lucide="users-round"></i>付费用户
          </button>
          <button class="nav-item" data-view="leaderboard" type="button">
            <i data-lucide="trophy"></i>排行榜治理
          </button>
          <div class="nav-label">系统</div>
          <button class="nav-item" data-view="settings" type="button">
            <i data-lucide="sliders-horizontal"></i>服务设置
          </button>
          <button class="nav-item" data-view="account" type="button">
            <i data-lucide="shield-check"></i>管理员安全
          </button>
          <button class="nav-item" data-view="audit" type="button">
            <i data-lucide="scroll-text"></i>审计日志
          </button>
        </div>
        <div class="sidebar-foot">
          <div class="admin-card">
            <span class="avatar" id="admin-avatar">OP</span>
            <span>
              <b id="admin-name">${esc(adminLabel)}</b>
              <span id="admin-role">${esc(adminSubtitle)}</span>
            </span>
            <button type="button" id="btn-logout" title="退出登录">
              <i data-lucide="log-out"></i>
            </button>
          </div>
        </div>
      </aside>

      <section class="workspace">
        <header class="topbar">
          <div class="crumb">
            <span>Velo Ops</span>
            <i data-lucide="chevron-right"></i>
            <b id="crumb-title">${TAB_NAMES[currentTab]}</b>
          </div>
          <div class="top-search">
            <i data-lucide="search"></i>
            <input id="global-search" placeholder="搜索邮箱、许可证或设备 ID" />
            <kbd>⌘ K</kbd>
          </div>
          <button class="icon-btn" type="button" id="theme-toggle" title="切换主题">
            <i data-lucide="${themeIcon()}"></i>
          </button>
        </header>
        <main class="content" id="panel"></main>
      </section>
    </div>`;
    icons();
    ensureToastEl();
    ensureModalLayer();

    const onGlobalKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        app.querySelector<HTMLInputElement>("#global-search")?.focus();
      }
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onGlobalKey);

    app.querySelector("#btn-logout")!.addEventListener("click", () => {
      document.removeEventListener("keydown", onGlobalKey);
      void (async () => {
        try {
          if (getToken().startsWith("admses_")) {
            await adminApi("/admin/auth/logout", { method: "POST" });
          }
        } catch {
          /* ignore */
        }
        clearSession();
        void renderLogin();
      })();
    });

    app.querySelector("#theme-toggle")!.addEventListener("click", toggleTheme);

    app.querySelectorAll<HTMLButtonElement>(".nav-item[data-view]").forEach((b) => {
      b.addEventListener("click", () => {
        currentTab = b.dataset.view as Tab;
        void showTab(currentTab);
      });
    });

    const gs = app.querySelector<HTMLInputElement>("#global-search");
    gs?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        const q = gs.value.trim();
        if (!q) return;
        currentTab = "users";
        void showTab(currentTab).then(() => {
          const us = app.querySelector<HTMLInputElement>("#user-search");
          if (us) {
            us.value = q;
            us.dispatchEvent(new Event("input"));
          }
          const ue = app.querySelector<HTMLInputElement>("#user-email");
          if (ue && q.includes("@")) {
            ue.value = q;
            (app.querySelector("#btn-lookup") as HTMLButtonElement | null)?.click();
          }
        });
      }
    });

    void loadAdminMe();
    void showTab(currentTab);
  };

  async function loadAdminMe() {
    try {
      const me = await adminApi("/admin/auth/me");
      if (me.mode === "token") {
        adminLabel = "breakglass";
        adminSubtitle = "ADMIN_TOKEN";
      } else {
        const a = (me.admin || {}) as Record<string, unknown>;
        adminLabel = String(a.username || "ops");
        adminSubtitle = a.totpEnabled ? "2FA · 超级管理员" : "超级管理员";
      }
      const nameEl = app.querySelector("#admin-name");
      const roleEl = app.querySelector("#admin-role");
      const av = app.querySelector("#admin-avatar");
      if (nameEl) nameEl.textContent = adminLabel;
      if (roleEl) roleEl.textContent = adminSubtitle;
      if (av) av.textContent = initials(adminLabel);
    } catch {
      /* ignore */
    }
  }

  async function showTab(t: Tab) {
    currentTab = t;
    app.querySelectorAll<HTMLButtonElement>(".nav-item[data-view]").forEach((b) => {
      b.classList.toggle("active", b.dataset.view === t);
    });
    const crumb = app.querySelector("#crumb-title");
    if (crumb) crumb.textContent = TAB_NAMES[t] || t;
    const panel = app.querySelector("#panel")!;
    panel.scrollTop = 0;
    if (t === "dashboard") await renderDash(panel);
    else if (t === "account") await renderAccount(panel);
    else if (t === "settings") await renderSettings(panel);
    else if (t === "codes") renderCodes(panel);
    else if (t === "users") renderUsers(panel);
    else if (t === "leaderboard") renderLeaderboard(panel);
    else await renderAudit(panel);
    icons();
  }

  /* ── Dashboard ── */
  async function renderDash(panel: Element) {
    panel.innerHTML = `
      <div class="view">
        <div class="page-head">
          <div>
            <h1>运营控制台</h1>
            <p>授权服务与运营状态概览 · ${esc(todayLabel())}</p>
          </div>
          <div class="page-actions">
            <span class="badge" id="dash-env-badge"><i class="dot"></i>加载中…</span>
            <button class="btn sm" type="button" id="btn-dash-refresh">
              <i data-lucide="refresh-cw"></i>刷新
            </button>
          </div>
        </div>
        <div class="stats" id="dash-stats">
          <article class="stat">
            <div class="stat-label"><span>存储</span><i data-lucide="database"></i></div>
            <strong>—</strong>
            <div class="stat-foot"><span>加载中</span></div>
          </article>
        </div>
        <div class="grid-2">
          <section class="panel">
            <div class="panel-head">
              <div class="panel-title"><h2>服务健康</h2><p>外部依赖与关键配置</p></div>
              <button class="btn sm" type="button" data-go="settings">检查配置</button>
            </div>
            <div class="panel-body"><div class="health-list" id="dash-health"><p class="muted">加载中…</p></div></div>
          </section>
          <section class="panel">
            <div class="panel-head">
              <div class="panel-title"><h2>最近事件</h2><p>高信号运营动态</p></div>
              <button class="btn ghost sm" type="button" data-go="audit">全部日志</button>
            </div>
            <div class="panel-body"><div class="event-list" id="dash-events"><p class="muted">加载中…</p></div></div>
          </section>
        </div>
      </div>`;
    icons();

    panel.querySelectorAll<HTMLButtonElement>("[data-go]").forEach((b) => {
      b.addEventListener("click", () => {
        currentTab = b.dataset.go as Tab;
        void showTab(currentTab);
      });
    });
    panel.querySelector("#btn-dash-refresh")!.addEventListener("click", () =>
      renderDash(panel),
    );

    try {
      const h = await adminApi("/admin/health");
      let st: Record<string, unknown> = { ...h };
      try {
        const s = await adminApi("/admin/settings");
        st = { ...(s.status as object), ...h, ...(s.status as object) } as Record<
          string,
          unknown
        >;
        if (s.status) st = { ...h, ...(s.status as object) };
      } catch {
        /* health only */
      }

      const badge = panel.querySelector("#dash-env-badge")!;
      const ok = Boolean(st.admin !== false);
      badge.className = "badge " + (ok ? "green" : "red");
      badge.innerHTML = `<i class="dot"></i>${ok ? "服务可达" : "异常"}`;

      (panel.querySelector("#dash-stats") as HTMLElement).innerHTML = `
        <article class="stat">
          <div class="stat-label"><span>存储后端</span><i data-lucide="database"></i></div>
          <strong>${esc(st.storage || "—")}</strong>
          <div class="stat-foot"><span>D1 / 内存等</span></div>
        </article>
        <article class="stat">
          <div class="stat-label"><span>SMTP</span><i data-lucide="mail"></i></div>
          <strong>${st.smtp ? "已配置" : "未配置"}</strong>
          <div class="stat-foot"><span>Magic Link / 通知</span></div>
        </article>
        <article class="stat">
          <div class="stat-label"><span>Stripe</span><i data-lucide="credit-card"></i></div>
          <strong>${st.stripe ? "已配置" : "未配置"}</strong>
          <div class="stat-foot"><span>Webhook / Checkout</span></div>
        </article>
        <article class="stat">
          <div class="stat-label"><span>DEV_AUTH</span><i data-lucide="flask-conical"></i></div>
          <strong>${st.devAuth ? "开启" : "关闭"}</strong>
          <div class="stat-foot"><span>${st.devAuth ? "生产请保持关闭" : "生产推荐状态"}</span></div>
        </article>`;

      const healthCls = (good: boolean, warn = false) =>
        good ? "" : warn ? " warn" : " bad";
      const healthBadge = (good: boolean, warnLabel?: string) =>
        good
          ? `<span class="badge green">正常</span>`
          : warnLabel
            ? `<span class="badge yellow">${warnLabel}</span>`
            : `<span class="badge red">未配置</span>`;

      (panel.querySelector("#dash-health") as HTMLElement).innerHTML = `
        <div class="health${healthCls(true)}">
          <span class="health-icon"><i data-lucide="cloud-check"></i></span>
          <span><b>License API</b><small>${esc(st.publicOrigin || getApiBase())}</small></span>
          <span class="badge green">正常</span>
        </div>
        <div class="health${healthCls(Boolean(st.smtp))}">
          <span class="health-icon"><i data-lucide="mail-check"></i></span>
          <span><b>SMTP</b><small>${st.smtp ? "已配置" : "未配置邮件发送"}</small></span>
          ${healthBadge(Boolean(st.smtp))}
        </div>
        <div class="health${healthCls(Boolean(st.stripe))}">
          <span class="health-icon"><i data-lucide="credit-card"></i></span>
          <span><b>Stripe</b><small>${st.stripe ? "密钥已配置" : "未配置支付"}</small></span>
          ${healthBadge(Boolean(st.stripe))}
        </div>
        <div class="health${healthCls(!st.devAuth, Boolean(st.devAuth))}">
          <span class="health-icon"><i data-lucide="flask-conical"></i></span>
          <span><b>DEV_AUTH</b><small>生产环境应保持关闭</small></span>
          ${st.devAuth ? `<span class="badge yellow">开启</span>` : `<span class="badge green">关闭</span>`}
        </div>`;

      // recent audit
      try {
        const audit = await adminApi("/admin/audit?limit=6");
        const entries = (audit.entries as Array<Record<string, unknown>>) || [];
        const el = panel.querySelector("#dash-events") as HTMLElement;
        if (!entries.length) {
          el.innerHTML = `<p class="muted">暂无审计记录</p>`;
        } else {
          el.innerHTML = entries
            .map((e) => {
              const at = String(e.at || "").replace("T", " ").slice(0, 16);
              const action = String(e.action || "event");
              const detail = e.detail ? JSON.stringify(e.detail).slice(0, 80) : "";
              return `<div class="event">
                <span class="event-icon"><i data-lucide="scroll-text"></i></span>
                <span><b>${esc(action)}</b><small>${esc(detail)}</small></span>
                <time>${esc(at.slice(11) || at)}</time>
              </div>`;
            })
            .join("");
        }
      } catch {
        (panel.querySelector("#dash-events") as HTMLElement).innerHTML =
          `<p class="muted">无法加载审计</p>`;
      }

      icons();
      toast("总览已刷新", true);
    } catch (e) {
      toast(String((e as Error).message || e), false);
    }
  }

  /* ── Account security ── */
  async function renderAccount(panel: Element) {
    panel.innerHTML = `
      <div class="view">
        <div class="page-head">
          <div><h1>管理员安全</h1><p>加载账号信息…</p></div>
        </div>
      </div>`;

    try {
      const me = await adminApi("/admin/auth/me");
      if (me.mode === "token") {
        panel.innerHTML = `
          <div class="view">
            <div class="page-head">
              <div>
                <h1>管理员安全</h1>
                <p>当前为破窗会话</p>
              </div>
              <span class="badge orange">ADMIN_TOKEN</span>
            </div>
            <div class="notice">
              <i data-lucide="shield-alert"></i>
              <span>当前为 <strong>ADMIN_TOKEN 破窗</strong> 会话，无个人账号设置。请创建/使用运营账号登录以配置 2FA、操作密码与邮箱。</span>
            </div>
          </div>`;
        icons();
        return;
      }

      const a = (me.admin || {}) as Record<string, unknown>;
      const totpOn = Boolean(a.totpEnabled);
      panel.innerHTML = `
        <div class="view">
          <div class="page-head">
            <div>
              <h1>管理员安全</h1>
              <p>管理员账户与高权限操作凭据分离管理。</p>
            </div>
            <span class="badge ${totpOn ? "green" : "yellow"}">
              <i class="dot"></i>2FA ${totpOn ? "已开启" : "未开启"}
            </span>
          </div>
          <div class="notice">
            <i data-lucide="shield-alert"></i>
            <span>密码、操作密码、邮箱与 2FA 的变更都会记录到审计日志。关闭 2FA 需要登录密码和当前验证码。</span>
          </div>
          <div class="security-grid">
            <article class="security-card">
              <div class="security-top">
                <span class="security-icon"><i data-lucide="user-round-cog"></i></span>
                <span class="badge mono">${esc(a.username)}</span>
              </div>
              <h2>登录账户</h2>
              <p>修改登录密码。邮箱：${a.emailBound ? esc(a.email) : "未绑定"}。</p>
              <div class="field">
                <label>当前密码</label>
                <input class="input" id="cur-pass" type="password" autocomplete="current-password" />
              </div>
              <div class="field">
                <label>新密码（≥10）</label>
                <input class="input" id="new-pass" type="password" autocomplete="new-password" />
              </div>
              <button class="btn sm primary" id="btn-pw" type="button">更新登录密码</button>
            </article>

            <article class="security-card">
              <div class="security-top">
                <span class="security-icon"><i data-lucide="key-square"></i></span>
                <span class="badge ${a.hasOpPassword ? "orange" : ""}">${a.hasOpPassword ? "已设置" : "未设置"}</span>
              </div>
              <h2>操作密码</h2>
              <p>用于批量撤销、人工授权和生产设置变更。</p>
              <div class="field">
                <label>登录密码（校验）</label>
                <input class="input" id="op-login" type="password" />
              </div>
              <div class="field">
                <label>新操作密码（留空可清除）</label>
                <input class="input" id="op-new" type="password" />
              </div>
              <div class="row">
                <button class="btn sm primary" id="btn-op" type="button">设置</button>
                <button class="btn sm" id="btn-op-clear" type="button">清除</button>
              </div>
              <div class="field" style="margin-top:10px">
                <label>缓存操作密码到本会话</label>
                <input class="input" id="op-cache" type="password" placeholder="可选 · 退出清除" />
              </div>
              <button class="btn sm" id="btn-op-cache" type="button">缓存到 session</button>
            </article>

            <article class="security-card">
              <div class="security-top">
                <span class="security-icon"><i data-lucide="mail"></i></span>
                <span class="badge ${a.emailBound ? "green" : ""}">${a.emailBound ? "已绑定" : "未绑定"}</span>
              </div>
              <h2>绑定邮箱</h2>
              <p>加密存储于 D1（与用户 PII 同密钥）。</p>
              <div class="field">
                <label>登录密码</label>
                <input class="input" id="em-pass" type="password" />
              </div>
              <div class="field">
                <label>邮箱</label>
                <input class="input" id="em-addr" type="email" value="${esc(a.email || "")}" />
              </div>
              <button class="btn sm primary" id="btn-em" type="button">绑定邮箱</button>
            </article>

            <article class="security-card">
              <div class="security-top">
                <span class="security-icon"><i data-lucide="scan-line"></i></span>
                <span class="badge ${totpOn ? "green" : "yellow"}">${totpOn ? "已开启" : "未开启"}</span>
              </div>
              <h2>两步验证</h2>
              <p>Authenticator App（Google / 1Password / Authy）提供 6 位动态码。</p>
              <div class="row" style="margin-bottom:8px">
                <button class="btn sm primary" id="btn-totp-setup" type="button">开始设置 / 重置密钥</button>
              </div>
              <div id="totp-box"></div>
              <div class="field">
                <label>验证码</label>
                <input class="input mono" id="totp-code" type="text" inputmode="numeric" placeholder="6 位" />
              </div>
              <div class="field">
                <label>关闭时需登录密码</label>
                <input class="input" id="totp-pass" type="password" />
              </div>
              <div class="row">
                <button class="btn sm primary" id="btn-totp-on" type="button">启用 2FA</button>
                <button class="btn sm danger" id="btn-totp-off" type="button">关闭 2FA</button>
              </div>
            </article>
          </div>
        </div>`;
      icons();

      app.querySelector("#btn-pw")!.addEventListener("click", async () => {
        try {
          await adminApi("/admin/auth/password", {
            method: "POST",
            json: {
              currentPassword: (app.querySelector("#cur-pass") as HTMLInputElement)
                .value,
              newPassword: (app.querySelector("#new-pass") as HTMLInputElement).value,
            },
          });
          toast("登录密码已更新", true);
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      app.querySelector("#btn-op")!.addEventListener("click", async () => {
        try {
          const op = (app.querySelector("#op-new") as HTMLInputElement).value;
          await adminApi("/admin/auth/op-password", {
            method: "POST",
            json: {
              loginPassword: (app.querySelector("#op-login") as HTMLInputElement)
                .value,
              opPassword: op,
            },
          });
          if (op) setOpPassword(op);
          toast("操作密码已设置", true);
          await renderAccount(panel);
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      app.querySelector("#btn-op-clear")!.addEventListener("click", async () => {
        try {
          await adminApi("/admin/auth/op-password", {
            method: "POST",
            json: {
              loginPassword: (app.querySelector("#op-login") as HTMLInputElement)
                .value,
              clear: true,
            },
          });
          setOpPassword("");
          toast("操作密码已清除", true);
          await renderAccount(panel);
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      app.querySelector("#btn-op-cache")!.addEventListener("click", () => {
        setOpPassword((app.querySelector("#op-cache") as HTMLInputElement).value);
        toast("已缓存操作密码到 sessionStorage（退出清除）", true);
      });

      app.querySelector("#btn-em")!.addEventListener("click", async () => {
        try {
          await adminApi("/admin/auth/email", {
            method: "POST",
            json: {
              loginPassword: (app.querySelector("#em-pass") as HTMLInputElement)
                .value,
              email: (app.querySelector("#em-addr") as HTMLInputElement).value,
            },
          });
          toast("邮箱已绑定", true);
          await renderAccount(panel);
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      app.querySelector("#btn-totp-setup")!.addEventListener("click", async () => {
        try {
          const r = await adminApi("/admin/auth/2fa/setup", { method: "POST" });
          const secret = String(r.secret || "");
          const otpauth = String(r.otpauth || "");
          const box = app.querySelector("#totp-box")!;
          box.innerHTML = `
            <div class="totp-setup">
              <div class="totp-qr-wrap">
                <canvas id="totp-qr" width="180" height="180" aria-label="2FA QR code"></canvas>
              </div>
              <div class="totp-meta">
                <p class="muted" style="margin:0 0 8px;font-size:8px">用 Authenticator 扫描二维码，或手动输入密钥。</p>
                <p style="margin:0;font-size:8.5px">密钥：
                  <code class="mono" id="totp-secret-text">${esc(secret)}</code>
                  <button class="btn ghost sm" id="btn-copy-totp-secret" type="button">复制</button>
                </p>
                <p class="muted" style="margin-top:8px;font-size:8px">扫码后输入 6 位验证码，点「启用 2FA」。</p>
              </div>
            </div>`;
          const canvas = box.querySelector<HTMLCanvasElement>("#totp-qr");
          if (canvas && otpauth) {
            await QRCode.toCanvas(canvas, otpauth, {
              width: 180,
              margin: 2,
              errorCorrectionLevel: "M",
              color: { dark: "#0a0a0c", light: "#ffffff" },
            });
          }
          box.querySelector("#btn-copy-totp-secret")?.addEventListener("click", async () => {
            try {
              await navigator.clipboard.writeText(secret);
              toast("密钥已复制", true);
            } catch {
              toast(secret, true);
            }
          });
          icons();
          toast("请扫描二维码并输入验证码以启用", true);
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      app.querySelector("#btn-totp-on")!.addEventListener("click", async () => {
        try {
          await adminApi("/admin/auth/2fa/enable", {
            method: "POST",
            json: {
              code: (app.querySelector("#totp-code") as HTMLInputElement).value,
            },
          });
          toast("2FA 已启用", true);
          await renderAccount(panel);
          void loadAdminMe();
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      app.querySelector("#btn-totp-off")!.addEventListener("click", async () => {
        try {
          await adminApi("/admin/auth/2fa/disable", {
            method: "POST",
            json: {
              loginPassword: (app.querySelector("#totp-pass") as HTMLInputElement)
                .value,
              code: (app.querySelector("#totp-code") as HTMLInputElement).value,
            },
          });
          toast("2FA 已关闭", true);
          await renderAccount(panel);
          void loadAdminMe();
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      toast("账号安全已加载", true);
    } catch (e) {
      toast(String((e as Error).message || e), false);
    }
  }

  /* ── Settings ── */
  async function renderSettings(panel: Element) {
    panel.innerHTML = `
      <div class="view">
        <div class="page-head">
          <div><h1>服务设置</h1><p>加载中…</p></div>
        </div>
      </div>`;

    try {
      const data = await adminApi("/admin/settings");
      const v = (data.values || {}) as Record<string, string>;
      const sources = (data.sources || {}) as Record<string, string>;

      const field = (
        id: string,
        label: string,
        type = "text",
        full = false,
      ) => {
        const src = sources[id] || "unset";
        const isPw = type === "password";
        return `
          <div class="field${full ? " full" : ""}">
            <label>${label} <span class="source">${esc(src)}</span></label>
            <input class="input mono" id="${id}" type="${type}"
              value="${esc(isPw ? "" : v[id] || "")}"
              placeholder="${isPw ? (v[id] ? "已设置 · 留空不修改 · 填 __clear__ 清除" : "未设置") : ""}" />
          </div>`;
      };

      panel.innerHTML = `
        <div class="view">
          <div class="page-head">
            <div>
              <h1>服务设置</h1>
              <p>配置站点、邮件、支付和开发环境开关。敏感值不会回显。</p>
            </div>
            <span class="badge orange"><i data-lucide="shield-alert"></i>高权限区域</span>
          </div>
          <div class="settings-layout">
            <nav class="setting-nav">
              <button class="setting-tab active" type="button" data-setting="site">
                <i data-lucide="globe-2"></i>站点
              </button>
              <button class="setting-tab" type="button" data-setting="smtp">
                <i data-lucide="mail"></i>邮件 SMTP
              </button>
              <button class="setting-tab" type="button" data-setting="stripe">
                <i data-lucide="credit-card"></i>Stripe
              </button>
              <button class="setting-tab" type="button" data-setting="dev">
                <i data-lucide="flask-conical"></i>开发环境
              </button>
            </nav>
            <div>
              <div class="setting-page active" id="setting-site">
                <section class="form-section">
                  <div class="form-head">
                    <h2>公开站点</h2>
                    <p>授权站与邮件中使用的公开地址</p>
                  </div>
                  <div class="form-body">
                    ${field("publicOrigin", "PUBLIC_ORIGIN（Magic Link / Checkout 回跳）", "text", true)}
                  </div>
                  <div class="form-actions">
                    <button class="btn" type="button" id="btn-reload-settings">重新加载</button>
                    <button class="btn primary" type="button" id="btn-save-site">保存站点设置</button>
                  </div>
                </section>
              </div>

              <div class="setting-page" id="setting-smtp">
                <section class="form-section">
                  <div class="form-head">
                    <h2>SMTP 邮件</h2>
                    <p>Magic Link 和系统通知使用</p>
                  </div>
                  <div class="form-body">
                    ${field("smtpHost", "SMTP_HOST")}
                    ${field("smtpPort", "SMTP_PORT（默认 465）")}
                    ${field("smtpSecure", "SMTP_SECURE（tls / starttls / off）")}
                    ${field("smtpUser", "SMTP_USER")}
                    ${field("smtpPass", "SMTP_PASS", "password")}
                    ${field("mailFrom", "MAIL_FROM", "text", true)}
                    <div class="field full">
                      <label>测试收件人</label>
                      <input class="input" id="test-to" type="email" placeholder="you@example.com" />
                    </div>
                  </div>
                  <div class="form-actions">
                    <button class="btn" type="button" id="btn-test-smtp">
                      <i data-lucide="send"></i>发送测试邮件
                    </button>
                    <button class="btn primary" type="button" id="btn-save-smtp">保存邮件设置</button>
                  </div>
                </section>
              </div>

              <div class="setting-page" id="setting-stripe">
                <section class="form-section">
                  <div class="form-head">
                    <h2>Stripe 支付</h2>
                    <p>永久授权结账与 Webhook</p>
                  </div>
                  <div class="form-body">
                    ${field("stripeSecretKey", "STRIPE_SECRET_KEY", "password", true)}
                    ${field("stripeWebhookSecret", "STRIPE_WEBHOOK_SECRET", "password", true)}
                    ${field("stripePriceLifetime", "STRIPE_PRICE_LIFETIME", "text", true)}
                  </div>
                  <div class="form-actions">
                    <button class="btn primary" type="button" id="btn-save-stripe">保存 Stripe 设置</button>
                  </div>
                </section>
              </div>

              <div class="setting-page" id="setting-dev">
                <div class="notice">
                  <i data-lucide="triangle-alert"></i>
                  <span>开发开关会改变认证行为。生产环境启用前请谨慎操作，变更会写入审计。</span>
                </div>
                <section class="form-section">
                  <div class="form-head">
                    <h2>开发认证</h2>
                    <p>仅供本地或隔离测试环境使用</p>
                  </div>
                  <div class="form-body">
                    ${field("devAuth", "DEV_AUTH（1=开 / 0=关，空=跟随 env）", "text", true)}
                    <div class="field full">
                      <small>密钥类字段：界面不回显明文；保存时留空表示保持原值。填 <code class="mono">__clear__</code> 可清除密钥。</small>
                    </div>
                  </div>
                  <div class="form-actions">
                    <button class="btn primary" type="button" id="btn-save-dev">保存开发设置</button>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>`;
      icons();

      panel.querySelectorAll<HTMLButtonElement>(".setting-tab").forEach((b) => {
        b.addEventListener("click", () => {
          panel
            .querySelectorAll(".setting-tab")
            .forEach((x) => x.classList.toggle("active", x === b));
          panel.querySelectorAll(".setting-page").forEach((p) => {
            p.classList.toggle(
              "active",
              p.id === `setting-${b.dataset.setting}`,
            );
          });
        });
      });

      const readPatch = () => {
        const ids = [
          "publicOrigin",
          "devAuth",
          "smtpHost",
          "smtpPort",
          "smtpSecure",
          "smtpUser",
          "smtpPass",
          "mailFrom",
          "stripeSecretKey",
          "stripeWebhookSecret",
          "stripePriceLifetime",
        ];
        const patch: Record<string, string> = {};
        for (const id of ids) {
          const el = app.querySelector<HTMLInputElement>(`#${id}`);
          if (!el) continue;
          if (el.type === "password") {
            if (el.value.trim()) patch[id] = el.value.trim();
          } else {
            patch[id] = el.value;
          }
        }
        return patch;
      };

      const saveSettings = async () => {
        await adminApi("/admin/settings", {
          method: "PUT",
          json: readPatch(),
        });
        toast("设置已保存", true);
        await renderSettings(panel);
      };

      for (const id of [
        "btn-save-site",
        "btn-save-smtp",
        "btn-save-stripe",
        "btn-save-dev",
      ]) {
        app.querySelector(`#${id}`)?.addEventListener("click", () => {
          void saveSettings().catch((e) =>
            toast(String((e as Error).message || e), false),
          );
        });
      }

      app.querySelector("#btn-reload-settings")!.addEventListener("click", () =>
        renderSettings(panel),
      );

      app.querySelector("#btn-test-smtp")!.addEventListener("click", async () => {
        try {
          await adminApi("/admin/settings", {
            method: "PUT",
            json: readPatch(),
          });
          const to = (app.querySelector("#test-to") as HTMLInputElement).value.trim();
          const res = await adminApi("/admin/settings/test-smtp", {
            method: "POST",
            json: { to },
          });
          toast(String(res.message || "已发送"), true);
        } catch (e) {
          toast(String((e as Error).message || e), false);
        }
      });

      toast("设置已加载", true);
    } catch (e) {
      toast(String((e as Error).message || e), false);
    }
  }

  /* ── Codes ── */
  function renderCodes(panel: Element) {
    panel.innerHTML = `
      <div class="view">
        <div class="page-head">
          <div>
            <h1>兑换码</h1>
            <p>生成、筛选、导出与批量撤销兑换码。</p>
          </div>
          <div class="page-actions">
            <button class="btn" type="button" id="btn-csv-page">
              <i data-lucide="download"></i>导出本页 CSV
            </button>
            <button class="btn primary" type="button" id="btn-gen-open">
              <i data-lucide="plus"></i>生成兑换码
            </button>
          </div>
        </div>
        <div class="bulkbar" id="bulkbar">
          <i data-lucide="check-square-2"></i>
          <span>已选择 <b id="selected-count">0</b> 项</span>
          <button class="btn sm" type="button" id="btn-copy-selected">
            <i data-lucide="copy"></i>复制
          </button>
          <button class="btn danger sm" type="button" id="btn-bulk-sel">
            <i data-lucide="ban"></i>批量撤销
          </button>
        </div>
        <div id="gen-result-box" class="codes-result hidden"></div>
        <section class="panel">
          <div class="toolbar">
            <div class="search">
              <i data-lucide="search"></i>
              <input class="input" id="code-search" placeholder="搜索兑换码或批次" />
            </div>
            <select class="select" id="code-status">
              <option value="">全部状态</option>
              <option value="unused">未使用</option>
              <option value="redeemed">已兑换</option>
              <option value="revoked">已撤销</option>
            </select>
            <input class="input mono" id="code-batch" style="width:140px;flex:0 0 auto" placeholder="batchId" />
            <button class="btn sm" type="button" id="btn-load-codes">
              <i data-lucide="refresh-cw"></i>加载
            </button>
            <button class="btn danger sm" type="button" id="btn-bulk-batch" title="作废该 batch 全部 unused">
              作废 batch
            </button>
          </div>
          <div class="table-wrap">
            <table class="table" id="codes-table">
              <thead>
                <tr>
                  <th><input type="checkbox" id="chk-all" /></th>
                  <th>兑换码</th>
                  <th>授权</th>
                  <th>状态</th>
                  <th>批次</th>
                  <th>备注</th>
                  <th></th>
                </tr>
              </thead>
              <tbody id="codes-body"></tbody>
            </table>
          </div>
          <div class="pagination">
            <span id="codes-meta">—</span>
            <div class="pager">
              <button type="button" id="btn-prev" title="上一页"><i data-lucide="chevron-left"></i></button>
              <button type="button" id="btn-next" title="下一页"><i data-lucide="chevron-right"></i></button>
            </div>
          </div>
        </section>
      </div>`;
    icons();

    const updateBulk = () => {
      const checked = [
        ...app.querySelectorAll<HTMLInputElement>(".chk-code:checked"),
      ];
      const bar = app.querySelector("#bulkbar");
      const cnt = app.querySelector("#selected-count");
      if (cnt) cnt.textContent = String(checked.length);
      bar?.classList.toggle("show", checked.length > 0);
      app.querySelectorAll("#codes-body tr").forEach((r) => {
        const c = r.querySelector<HTMLInputElement>(".chk-code");
        r.classList.toggle("selected", Boolean(c?.checked));
      });
    };

    const loadCodes = async () => {
      try {
        const status = (app.querySelector("#code-status") as HTMLSelectElement)
          .value;
        const batchId = (
          app.querySelector("#code-batch") as HTMLInputElement
        ).value.trim();
        const params = new URLSearchParams({
          limit: String(pageSize),
          offset: String(codesPage * pageSize),
        });
        if (status) params.set("status", status);
        if (batchId) params.set("batchId", batchId);
        const data = await adminApi(`/admin/codes?${params}`);
        const total = Number(data.total) || 0;
        pageCodes = (data.codes as Array<Record<string, unknown>>) || [];
        const q = (
          app.querySelector("#code-search") as HTMLInputElement
        ).value
          .trim()
          .toLowerCase();
        const filtered = q
          ? pageCodes.filter((c) =>
              `${c.code} ${c.batchId} ${c.note}`.toLowerCase().includes(q),
            )
          : pageCodes;

        const body = app.querySelector("#codes-body")!;
        body.innerHTML = filtered.length
          ? filtered
              .map((c) => {
                const st = String(c.status);
                const tpl = (c.template || {}) as Record<string, unknown>;
                const plan = planLabel(tpl.plan);
                const devices = tpl.maxDevices != null ? ` · ${tpl.maxDevices} 台` : "";
                return `<tr data-status="${esc(st)}">
                  <td>${
                    st === "unused"
                      ? `<input type="checkbox" class="chk-code" value="${esc(c.code)}" />`
                      : ""
                  }</td>
                  <td class="mono"><b>${esc(c.code)}</b></td>
                  <td>${esc(plan)}${esc(devices)}</td>
                  <td>${statusBadge(st)}</td>
                  <td class="mono muted">${esc(c.batchId)}</td>
                  <td class="muted">${esc(c.note || "—")}</td>
                  <td>
                    <div class="row-actions">
                      <button class="icon-btn sm copy-code" type="button" data-code="${esc(c.code)}" title="复制">
                        <i data-lucide="copy"></i>
                      </button>
                      ${
                        st === "unused"
                          ? `<button class="icon-btn sm" type="button" data-revoke="${esc(c.code)}" title="撤销">
                              <i data-lucide="ban"></i>
                            </button>`
                          : ""
                      }
                    </div>
                  </td>
                </tr>`;
              })
              .join("")
          : `<tr><td colspan="7" class="muted">无数据</td></tr>`;

        body.querySelectorAll<HTMLInputElement>(".chk-code").forEach((c) => {
          c.onchange = updateBulk;
        });
        body.querySelectorAll<HTMLButtonElement>(".copy-code").forEach((btn) => {
          btn.onclick = async () => {
            const code = btn.dataset.code || "";
            try {
              await navigator.clipboard.writeText(code);
              toast("兑换码已复制", true);
            } catch {
              toast(code, true);
            }
          };
        });
        body.querySelectorAll<HTMLButtonElement>("[data-revoke]").forEach((btn) => {
          btn.onclick = () => {
            const code = btn.dataset.revoke || "";
            openModal(
              "撤销兑换码",
              `<h3>撤销 ${esc(code)}？</h3><p>未使用的兑换码将立即失效。已兑换的许可证不会被自动撤销。</p>`,
              {
                confirmLabel: "确认撤销",
                danger: true,
                onConfirm: async () => {
                  try {
                    await adminApi("/admin/codes/revoke", {
                      method: "POST",
                      json: { code },
                    });
                    toast(`已作废 ${code}`, true);
                    await loadCodes();
                  } catch (e) {
                    toast(String((e as Error).message || e), false);
                  }
                },
              },
            );
          };
        });

        const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
        if (codesPage > maxPage) codesPage = maxPage;
        (app.querySelector("#codes-meta") as HTMLElement).textContent =
          `共 ${total} 条 · 本页 ${pageCodes.length} · 第 ${codesPage + 1} / ${maxPage + 1} 页`;
        updateBulk();
        icons();
        toast(`列表 ${pageCodes.length}/${total}`, true);
      } catch (e) {
        toast(String((e as Error).message || e), false);
      }
    };

    app.querySelector("#btn-load-codes")!.addEventListener("click", () => {
      codesPage = 0;
      void loadCodes();
    });
    app.querySelector("#code-status")!.addEventListener("change", () => {
      codesPage = 0;
      void loadCodes();
    });
    app.querySelector("#code-search")!.addEventListener("input", () => {
      void loadCodes();
    });
    app.querySelector("#btn-prev")!.addEventListener("click", () => {
      if (codesPage > 0) {
        codesPage--;
        void loadCodes();
      }
    });
    app.querySelector("#btn-next")!.addEventListener("click", () => {
      codesPage++;
      void loadCodes();
    });
    app.querySelector("#chk-all")!.addEventListener("change", (ev) => {
      const on = (ev.target as HTMLInputElement).checked;
      app.querySelectorAll<HTMLInputElement>(".chk-code").forEach((c) => {
        c.checked = on;
      });
      updateBulk();
    });

    app.querySelector("#btn-copy-selected")!.addEventListener("click", async () => {
      const codes = [
        ...app.querySelectorAll<HTMLInputElement>(".chk-code:checked"),
      ].map((c) => c.value);
      if (!codes.length) return toast("未勾选", false);
      try {
        await navigator.clipboard.writeText(codes.join("\n"));
        toast(`已复制 ${codes.length} 个兑换码`, true);
      } catch {
        toast(codes.join("\n"), true);
      }
    });

    app.querySelector("#btn-bulk-sel")!.addEventListener("click", () => {
      const codes = [
        ...app.querySelectorAll<HTMLInputElement>(".chk-code:checked"),
      ].map((c) => c.value);
      if (!codes.length) return toast("未勾选", false);
      openModal(
        "批量撤销兑换码",
        `<h3>撤销选中的 ${codes.length} 个兑换码？</h3><p>未使用的兑换码将立即失效。已兑换的许可证不会被自动撤销。</p>`,
        {
          confirmLabel: "确认撤销",
          danger: true,
          onConfirm: async () => {
            try {
              const data = await adminApi("/admin/codes/revoke-bulk", {
                method: "POST",
                json: { codes },
              });
              toast(
                `已作废 ${(data.revoked as string[])?.length ?? 0}`,
                true,
              );
              await loadCodes();
            } catch (e) {
              toast(String((e as Error).message || e), false);
            }
          },
        },
      );
    });

    app.querySelector("#btn-bulk-batch")!.addEventListener("click", () => {
      const batchId =
        (app.querySelector("#code-batch") as HTMLInputElement).value.trim() ||
        lastBatchId;
      if (!batchId) return toast("需要 batchId", false);
      openModal(
        "作废批次",
        `<h3>作废 batch <code class="mono">${esc(batchId)}</code> 全部 unused？</h3><p>仅撤销未使用的兑换码。</p>`,
        {
          confirmLabel: "确认作废",
          danger: true,
          onConfirm: async () => {
            try {
              const data = await adminApi("/admin/codes/revoke-bulk", {
                method: "POST",
                json: { batchId },
              });
              toast(
                `已作废 ${(data.revoked as string[])?.length ?? 0}`,
                true,
              );
              await loadCodes();
            } catch (e) {
              toast(String((e as Error).message || e), false);
            }
          },
        },
      );
    });

    app.querySelector("#btn-csv-page")!.addEventListener("click", () => {
      if (!pageCodes.length) return toast("无数据", false);
      downloadCsv("velo-codes-page.csv", [
        ["code", "plan", "status", "batchId", "note"],
        ...pageCodes.map((c) => {
          const tpl = (c.template || {}) as Record<string, unknown>;
          return [
            String(c.code),
            String(tpl.plan ?? ""),
            String(c.status ?? ""),
            String(c.batchId ?? ""),
            String(c.note ?? ""),
          ];
        }),
      ]);
      toast("CSV 已导出", true);
    });

    app.querySelector("#btn-gen-open")!.addEventListener("click", () => {
      openModal(
        "生成兑换码",
        `<div class="form-body" style="padding:0">
          <div class="field">
            <label>授权类型</label>
            <select class="select" id="gen-plan" style="width:100%">
              <option value="lifetime">Lifetime</option>
              <option value="sub_yearly">sub_yearly (Annual)</option>
              <option value="sub_quarterly">sub_quarterly</option>
              <option value="sub_monthly">sub_monthly</option>
            </select>
          </div>
          <div class="field">
            <label>生成数量</label>
            <input class="input" id="gen-count" type="number" value="5" min="1" max="200" />
          </div>
          <div class="field">
            <label>设备上限</label>
            <input class="input" id="gen-devices" type="number" value="1" min="1" max="20" />
          </div>
          <div class="field">
            <label>前缀</label>
            <input class="input mono" id="gen-prefix" type="text" value="VELO" />
          </div>
          <div class="field full">
            <label>备注 / 批次说明</label>
            <input class="input" id="gen-note" type="text" placeholder="渠道 / 订单号" />
          </div>
        </div>`,
        {
          confirmLabel: "生成",
          wide: true,
          onConfirm: async () => {
            const count = Number(
              modalField<HTMLInputElement>("gen-count")?.value || 5,
            );
            const plan =
              modalField<HTMLSelectElement>("gen-plan")?.value || "lifetime";
            const maxDevices = Number(
              modalField<HTMLInputElement>("gen-devices")?.value || 1,
            );
            const note = modalField<HTMLInputElement>("gen-note")?.value || "";
            const prefix =
              modalField<HTMLInputElement>("gen-prefix")?.value || "VELO";
            try {
              const data = await adminApi("/admin/codes/generate", {
                method: "POST",
                json: { count, plan, maxDevices, note, prefix },
              });
              lastCodes = (data.codes as string[]) || [];
              lastBatchId = String(data.batchId || "");
              const box = app.querySelector("#gen-result-box") as HTMLElement;
              if (box) {
                box.classList.remove("hidden");
                box.textContent =
                  `batch ${lastBatchId}\n` + lastCodes.join("\n");
              }
              const batchInput = app.querySelector(
                "#code-batch",
              ) as HTMLInputElement;
              if (batchInput) batchInput.value = lastBatchId;
              toast(`生成 ${lastCodes.length} 个`, true);
              codesPage = 0;
              await loadCodes();
            } catch (e) {
              toast(String((e as Error).message || e), false);
              throw e;
            }
          },
        },
      );
    });

    void loadCodes();
  }

  /* ── Users ── */
  function renderUsers(panel: Element) {
    let rosterOffset = 0;
    const rosterLimit = 50;
    let selectedEmail = "";

    panel.innerHTML = `
      <div class="view">
        <div class="page-head">
          <div>
            <h1>付费用户</h1>
            <p>查找账户、授权、设备，并执行人工授权操作。</p>
          </div>
          <button class="btn primary" type="button" id="btn-grant-open">
            <i data-lucide="badge-plus"></i>授予许可证
          </button>
        </div>
        <div class="layout-inspector">
          <section class="panel">
            <div class="toolbar">
              <div class="search">
                <i data-lucide="search"></i>
                <input class="input" id="user-search" placeholder="搜索邮箱" />
              </div>
              <input class="input" id="user-email" type="email" style="width:200px;flex:0 0 auto" placeholder="精确邮箱查询" />
              <button class="btn sm" type="button" id="btn-lookup">
                <i data-lucide="scan-search"></i>查询详情
              </button>
              <button class="btn sm" type="button" id="btn-roster-refresh">
                <i data-lucide="refresh-cw"></i>刷新
              </button>
            </div>
            <div class="table-wrap">
              <table class="table" id="users-table">
                <thead>
                  <tr>
                    <th>用户</th>
                    <th>许可证</th>
                    <th>设备用量</th>
                    <th>来源</th>
                    <th>最近到期</th>
                    <th>注册</th>
                  </tr>
                </thead>
                <tbody id="roster-body"></tbody>
              </table>
            </div>
            <div class="pagination">
              <span id="roster-meta">加载中…</span>
              <div class="pager">
                <button type="button" id="btn-roster-prev"><i data-lucide="chevron-left"></i></button>
                <button type="button" id="btn-roster-next"><i data-lucide="chevron-right"></i></button>
              </div>
            </div>
          </section>
          <aside class="panel inspector" id="inspector">
            <div class="user-hero">
              <span class="user-avatar" id="user-initial">—</span>
              <span>
                <b id="user-name">选择用户</b>
                <span id="user-email-label">从左侧列表点击邮箱</span>
              </span>
            </div>
            <div class="inspector-section">
              <div class="section-label"><span>账户详情</span></div>
              <div id="user-panel" class="muted" style="font-size:8.5px">从列表点选用户，或输入邮箱查询。</div>
            </div>
          </aside>
        </div>
        <!-- hidden grant fields for programmatic fill -->
        <select id="grant-plan" class="hidden">
          <option value="lifetime">lifetime</option>
          <option value="sub_yearly">sub_yearly</option>
          <option value="sub_quarterly">sub_quarterly</option>
          <option value="sub_monthly">sub_monthly</option>
        </select>
        <input id="grant-devices" type="hidden" value="1" />
      </div>`;
    icons();

    const loadRoster = async () => {
      const body = app.querySelector("#roster-body")!;
      const meta = app.querySelector("#roster-meta")!;
      try {
        const data = await adminApi(
          `/admin/users?paid=1&limit=${rosterLimit}&offset=${rosterOffset}`,
        );
        const users = (data.users as Array<Record<string, unknown>>) || [];
        const total = Number(data.total) || 0;
        const q = (
          app.querySelector("#user-search") as HTMLInputElement
        ).value
          .trim()
          .toLowerCase();
        const filtered = q
          ? users.filter((u) => String(u.email || "").toLowerCase().includes(q))
          : users;

        meta.textContent = `共 ${total} 名付费用户 · 本页 ${users.length} · offset ${rosterOffset}`;
        body.innerHTML = filtered.length
          ? filtered
              .map((u) => {
                const plans = (
                  (u.planLabels as string[]) ||
                  (u.plans as string[]) ||
                  []
                ).join(" · ");
                const sources = ((u.sources as string[]) || []).join(", ");
                const until = u.nearestValidUntil
                  ? String(u.nearestValidUntil).slice(0, 10)
                  : "—";
                const reg = u.createdAt ? String(u.createdAt).slice(0, 10) : "—";
                const email = String(u.email || "");
                const planBadges = (
                  (u.planLabels as string[]) ||
                  (u.plans as string[]) ||
                  []
                )
                  .map((p) => {
                    const lab = planLabel(p);
                    const cls = lab === "Lifetime" ? "blue" : "";
                    return `<span class="badge ${cls}">${esc(lab)}</span>`;
                  })
                  .join(" ");
                return `<tr data-email="${esc(email)}" class="${selectedEmail === email ? "selected" : ""}">
                  <td>
                    <div class="cell-main">
                      <b>${esc(email.split("@")[0] || email)}</b>
                      <span>${esc(email)}</span>
                    </div>
                  </td>
                  <td>${planBadges || esc(plans || "—")}</td>
                  <td class="mono">${esc(u.devicesBound)} / ${esc(u.devicesMax)}</td>
                  <td class="muted">${esc(sources || "—")}</td>
                  <td class="mono muted">${esc(until)}</td>
                  <td class="mono muted">${esc(reg)}</td>
                </tr>`;
              })
              .join("")
          : `<tr><td colspan="6" class="muted">暂无付费用户</td></tr>`;

        body.querySelectorAll<HTMLTableRowElement>("tr[data-email]").forEach((tr) => {
          tr.onclick = () => {
            const em = tr.dataset.email || "";
            selectedEmail = em;
            body
              .querySelectorAll("tr")
              .forEach((r) => r.classList.toggle("selected", r === tr));
            (app.querySelector("#user-email") as HTMLInputElement).value = em;
            void lookupUser(em);
          };
        });
        icons();
      } catch (e) {
        meta.textContent = "列表加载失败";
        body.innerHTML = `<tr><td colspan="6">${esc((e as Error).message || e)}</td></tr>`;
      }
    };

    const renderUserDetail = (
      data: Record<string, unknown>,
      highlightKey?: string,
    ) => {
      const licenses = (data.licenses as Array<Record<string, unknown>>) || [];
      const devices = ((data.devices as Record<string, unknown>)?.devices ||
        []) as Array<Record<string, unknown>>;
      const usage = data.devices as Record<string, unknown> | undefined;
      const email = String(data.email || "");
      selectedEmail = email;

      (app.querySelector("#user-initial") as HTMLElement).textContent =
        initials(email);
      (app.querySelector("#user-name") as HTMLElement).textContent =
        email.split("@")[0] || email;
      (app.querySelector("#user-email-label") as HTMLElement).textContent = email;

      const panelEl = app.querySelector("#user-panel")!;
      panelEl.innerHTML = `
        <div class="kv"><span>邮箱</span><span class="mono">${esc(email)}</span></div>
        <div class="kv"><span>设备</span><span class="mono">${esc(usage?.bound)} / ${esc(usage?.max)}</span></div>
        <div class="section-label" style="margin-top:12px">
          <span>许可证 · ${licenses.length}</span>
        </div>
        ${
          licenses.length
            ? licenses
                .map((l) => {
                  const key = String(l.licenseKey || l.id || "");
                  const hi = highlightKey && key === highlightKey;
                  return `<div class="license-card" style="${hi ? "border-color:var(--green)" : ""}">
                    <div style="display:flex;justify-content:space-between;align-items:center;gap:6px">
                      <b style="font-size:9px">${esc(l.planLabel || planLabel(l.plan))}</b>
                      <span class="badge ${String(l.plan).includes("life") ? "blue" : ""}">${esc(l.planLabel || planLabel(l.plan))}</span>
                    </div>
                    <div class="key">
                      <span>${esc(key)}</span>
                      <button class="btn ghost sm" type="button" data-copy-key="${esc(key)}">复制</button>
                    </div>
                    <div class="muted mono" style="margin-top:4px;font-size:7px">${esc(l.id)}</div>
                  </div>`;
                })
                .join("")
            : `<p class="muted" style="margin:0">无付费许可证</p>`
        }
        <div class="section-label" style="margin-top:12px">
          <span>绑定设备 · ${devices.length}</span>
        </div>
        ${
          devices.length
            ? devices
                .map(
                  (d) =>
                    `<div class="device-row">
                      <i data-lucide="laptop"></i>
                      <span>
                        <b>${esc(d.label || "设备")}</b>
                        <span class="mono">${esc(d.deviceId)}</span>
                      </span>
                      <button class="btn danger sm" type="button" data-dev="${esc(d.deviceId)}">反激活</button>
                    </div>`,
                )
                .join("")
            : `<p class="muted" style="margin:0">无设备</p>`
        }`;

      panelEl.querySelectorAll<HTMLButtonElement>("[data-copy-key]").forEach((btn) => {
        btn.onclick = async () => {
          const k = btn.dataset.copyKey || "";
          try {
            await navigator.clipboard.writeText(k);
            toast("已复制许可证密钥", true);
          } catch {
            toast(k, true);
          }
        };
      });
      panelEl.querySelectorAll<HTMLButtonElement>("[data-dev]").forEach((btn) => {
        btn.onclick = () => {
          const deviceId = btn.dataset.dev || "";
          openModal(
            "反激活用户设备",
            `<h3>移除设备？</h3><p class="mono muted">${esc(deviceId)}</p><p>该设备将失去授权，并为用户释放一个设备名额。</p>`,
            {
              confirmLabel: "确认反激活",
              danger: true,
              onConfirm: async () => {
                try {
                  await adminApi("/admin/devices/deactivate", {
                    method: "POST",
                    json: { email, deviceId },
                  });
                  toast("已反激活", true);
                  await lookupUser(email);
                } catch (e) {
                  toast(String((e as Error).message || e), false);
                }
              },
            },
          );
        };
      });
      icons();
    };

    const lookupUser = async (email: string) => {
      try {
        const data = await adminApi(
          `/admin/users?email=${encodeURIComponent(email)}`,
        );
        renderUserDetail(data);
        toast("已加载用户详情", true);
      } catch (e) {
        toast(String((e as Error).message || e), false);
      }
    };

    app.querySelector("#btn-roster-refresh")!.addEventListener("click", () => {
      void loadRoster().then(() => toast("列表已刷新", true));
    });
    app.querySelector("#btn-roster-prev")!.addEventListener("click", () => {
      rosterOffset = Math.max(0, rosterOffset - rosterLimit);
      void loadRoster();
    });
    app.querySelector("#btn-roster-next")!.addEventListener("click", () => {
      rosterOffset += rosterLimit;
      void loadRoster();
    });
    app.querySelector("#user-search")!.addEventListener("input", () => {
      void loadRoster();
    });
    app.querySelector("#btn-lookup")!.addEventListener("click", () => {
      const email = (
        app.querySelector("#user-email") as HTMLInputElement
      ).value.trim();
      if (!email) return toast("请输入邮箱", false);
      void lookupUser(email);
    });

    app.querySelector("#btn-grant-open")!.addEventListener("click", () => {
      const prefill = (
        app.querySelector("#user-email") as HTMLInputElement
      ).value.trim();
      openModal(
        "人工授予许可证",
        `<h3>为用户创建许可证</h3>
         <p>该操作需要操作密码（若已设置），并会写入审计日志。用户用「邮箱 + 密钥」在软件内激活。</p>
         <div class="field">
           <label>用户邮箱</label>
           <input class="input" id="modal-grant-email" type="email" value="${esc(prefill)}" />
         </div>
         <div class="form-body" style="padding:0;margin-top:4px">
           <div class="field">
             <label>授权类型</label>
             <select class="select" id="modal-grant-plan" style="width:100%">
               <option value="lifetime">lifetime</option>
               <option value="sub_yearly">sub_yearly</option>
               <option value="sub_quarterly">sub_quarterly</option>
               <option value="sub_monthly">sub_monthly</option>
             </select>
           </div>
           <div class="field">
             <label>设备上限</label>
             <input class="input" id="modal-grant-devices" type="number" value="1" min="1" max="20" />
           </div>
         </div>`,
        {
          confirmLabel: "确认授予",
          wide: true,
          onConfirm: async () => {
            const email =
              modalField<HTMLInputElement>("modal-grant-email")?.value.trim() ||
              "";
            const plan =
              modalField<HTMLSelectElement>("modal-grant-plan")?.value ||
              "lifetime";
            const maxDevices = Number(
              modalField<HTMLInputElement>("modal-grant-devices")?.value || 1,
            );
            if (!email) {
              toast("需要邮箱", false);
              throw new Error("需要邮箱");
            }
            try {
              const data = await adminApi("/admin/licenses/grant", {
                method: "POST",
                json: { email, plan, maxDevices },
              });
              const key = String(
                (data as { licenseKey?: string }).licenseKey ||
                  (data as { license?: { licenseKey?: string } }).license
                    ?.licenseKey ||
                  "",
              );
              (app.querySelector("#user-email") as HTMLInputElement).value =
                email;
              renderUserDetail(data, key || undefined);
              void loadRoster();
              toast(
                key
                  ? `已发放 · 密钥 ${key}`
                  : String(
                      (data as { message?: string }).message || "已发放",
                    ),
                true,
              );
            } catch (e) {
              toast(String((e as Error).message || e), false);
              throw e;
            }
          },
        },
      );
    });

    void loadRoster();
  }

  /* ── Leaderboard placeholder ── */
  function renderLeaderboard(panel: Element) {
    panel.innerHTML = `
      <div class="view">
        <div class="page-head">
          <div>
            <h1>排行榜治理</h1>
            <p>维护公开身份、参赛资格与成绩可信度。</p>
          </div>
          <span class="badge yellow">规划中</span>
        </div>
        <div class="placeholder-page">
          <div class="ph-icon"><i data-lucide="trophy"></i></div>
          <h2>排行榜治理 · 规划中</h2>
          <p>昵称整改、举报队列、申诉复核与治理规则将在后端 API 就绪后接入。当前不会调用任何虚构的管理接口。</p>
        </div>
      </div>`;
    icons();
  }

  /* ── Audit ── */
  async function renderAudit(panel: Element) {
    panel.innerHTML = `
      <div class="view">
        <div class="page-head">
          <div>
            <h1>审计日志</h1>
            <p>记录管理员登录、高权限操作与系统配置变更。</p>
          </div>
          <button class="btn" type="button" id="btn-load-audit">
            <i data-lucide="refresh-cw"></i>刷新
          </button>
        </div>
        <section class="panel">
          <div class="toolbar">
            <div class="search">
              <i data-lucide="search"></i>
              <input class="input" id="audit-search" placeholder="搜索操作、目标或详情" />
            </div>
          </div>
          <div class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>操作</th>
                  <th>详情</th>
                </tr>
              </thead>
              <tbody id="audit-body"></tbody>
            </table>
          </div>
          <div class="pagination">
            <span id="audit-meta">显示最近 50 条记录</span>
            <div class="pager"></div>
          </div>
        </section>
      </div>`;
    icons();

    let allEntries: Array<Record<string, unknown>> = [];

    const paint = () => {
      const q = (
        app.querySelector("#audit-search") as HTMLInputElement
      )?.value
        .trim()
        .toLowerCase();
      const entries = q
        ? allEntries.filter((e) =>
            `${e.action} ${JSON.stringify(e.detail || {})}`
              .toLowerCase()
              .includes(q),
          )
        : allEntries;
      (app.querySelector("#audit-body") as HTMLElement).innerHTML = entries.length
        ? entries
            .map((e) => {
              const action = String(e.action || "");
              let badgeCls = "";
              if (/revoke|disable|deactivate|delete/i.test(action))
                badgeCls = "red";
              else if (/grant|generate|enable|create/i.test(action))
                badgeCls = "blue";
              else if (/settings|update|password/i.test(action))
                badgeCls = "orange";
              return `<tr>
                <td class="mono">${esc(String(e.at || "").replace("T", " ").slice(0, 19))}</td>
                <td><span class="badge ${badgeCls}">${esc(action)}</span></td>
                <td class="mono muted" style="max-width:420px;overflow:hidden;text-overflow:ellipsis">${esc(JSON.stringify(e.detail || {}))}</td>
              </tr>`;
            })
            .join("")
        : `<tr><td colspan="3" class="muted">无记录</td></tr>`;
      (app.querySelector("#audit-meta") as HTMLElement).textContent =
        `显示 ${entries.length} / ${allEntries.length} 条`;
    };

    const load = async () => {
      try {
        const data = await adminApi("/admin/audit?limit=50");
        allEntries = (data.entries as Array<Record<string, unknown>>) || [];
        paint();
        toast(`审计 ${allEntries.length} 条`, true);
      } catch (e) {
        toast(String((e as Error).message || e), false);
      }
    };

    app.querySelector("#btn-load-audit")!.addEventListener("click", () => load());
    app.querySelector("#audit-search")!.addEventListener("input", paint);
    await load();
  }

  shell();
}

/* ── Boot ── */
initTheme();

if (getToken()) {
  adminApi("/admin/health")
    .then(() => renderConsole("dashboard"))
    .catch(() => {
      clearSession();
      void renderLogin();
    });
} else {
  void renderLogin();
}
